'use client'

import { useState, useImperativeHandle, forwardRef, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { AlertCircle, Settings, Plus, X } from 'lucide-react'
import type { VariableTypeInput } from '@/types/models/v2'

export interface VariableSelectorProps {
  availableVariables: VariableTypeInput[]
  onVariablesChange?: (variables: VariableValue[]) => void
}

export interface VariableValue {
  name: string
  value: string | number | boolean | null
}

export interface VariableSelectorRef {
  getSelectedVariables: () => VariableValue[]
  getSelectedCount: () => number
  clearAll: () => void
  setSelectedVariables: (variables: VariableValue[]) => void
  getVariablesHash: () => string
}

interface VariableBox {
  id: string
  selectedVariableId: string | null
  value: string | number | boolean | null
}

export const VariableSelector = forwardRef<VariableSelectorRef, VariableSelectorProps>(
  ({ availableVariables, onVariablesChange }, ref) => {
    const [variableBoxes, setVariableBoxes] = useState<VariableBox[]>([])
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Notify parent of variable changes
    useEffect(() => {
      if (onVariablesChange) {
        const selectedVariables = variableBoxes
          .filter((box) => box.selectedVariableId)
          .map((box) => {
            const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
            return {
              name: variable?.name || '',
              value: box.value,
            }
          })
          .filter((v) => v.name)
        onVariablesChange(selectedVariables)
      }
    }, [variableBoxes, availableVariables, onVariablesChange])

    useImperativeHandle(ref, () => ({
      getSelectedVariables: () => {
        return variableBoxes
          .filter((box) => box.selectedVariableId)
          .map((box) => {
            const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
            return {
              name: variable?.name || '',
              value: box.value,
            }
          })
          .filter((v) => v.name)
      },
      getSelectedCount: () => {
        return variableBoxes.filter((box) => box.selectedVariableId).length
      },
      clearAll: () => {
        setVariableBoxes([])
        setErrors({})
      },
      setSelectedVariables: (variables: VariableValue[]) => {
        const newBoxes: VariableBox[] = variables.map((variable) => {
          const matchingVar = availableVariables.find((v) => v.name === variable.name)
          return {
            id: crypto.randomUUID(),
            selectedVariableId: matchingVar?.id || null,
            value: variable.value,
          }
        })
        setVariableBoxes(newBoxes)
        setErrors({})
      },
      getVariablesHash: () => {
        return JSON.stringify(
          variableBoxes.map((box) => ({
            id: box.selectedVariableId,
            value: box.value,
          }))
        )
      },
    }))

    const notifyParent = (boxes: VariableBox[]) => {
      if (!onVariablesChange) return

      const selectedVariables = boxes
        .filter((box) => box.selectedVariableId)
        .map((box) => {
          const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
          return {
            name: variable?.name || '',
            value: box.value,
          }
        })
        .filter((v) => v.name)

      // Call onVariablesChange synchronously
      onVariablesChange(selectedVariables)
    }

    const updateVariableValue = (boxId: string, value: string | number | boolean | null) => {
      // Find the box and its variable
      const box = variableBoxes.find((b) => b.id === boxId)
      if (!box || !box.selectedVariableId) return

      const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
      if (!variable) return

      // Create new boxes array with updated value
      const newBoxes = variableBoxes.map((b) => (b.id === boxId ? { ...b, value } : b))

      // Update state and notify parent synchronously
      setVariableBoxes(newBoxes)
      notifyParent(newBoxes)

      // Clear error when value is updated
      if (errors[boxId]) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[boxId]
          return newErrors
        })
      }
    }

    const updateVariableSelection = (boxId: string, variableId: string | null) => {
      // Find the variable if selected
      const variable = variableId ? availableVariables.find((v) => v.id === variableId) : null

      // Create new boxes array with updated selection
      const newBoxes = variableBoxes.map((box) => {
        if (box.id === boxId) {
          return {
            ...box,
            selectedVariableId: variableId,
            value: variable?.default || null,
          }
        }
        return box
      })

      // Update state and notify parent synchronously
      setVariableBoxes(newBoxes)
      notifyParent(newBoxes)

      // Clear error when variable is changed
      if (errors[boxId]) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[boxId]
          return newErrors
        })
      }
    }

    const removeVariableBox = (boxId: string) => {
      // Create new boxes array without the removed box
      const newBoxes = variableBoxes.filter((box) => box.id !== boxId)

      // Update state and notify parent synchronously
      setVariableBoxes(newBoxes)
      notifyParent(newBoxes)

      // Clear any errors for this box
      if (errors[boxId]) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[boxId]
          return newErrors
        })
      }
    }

    const addVariableBox = () => {
      const newBox: VariableBox = {
        id: crypto.randomUUID(),
        selectedVariableId: null,
        value: null,
      }

      // Create new boxes array with the new box
      const newBoxes = [...variableBoxes, newBox]

      // Update state
      setVariableBoxes(newBoxes)
      // No need to notify parent as the new box has no selected variable
    }

    const validateValue = (
      variable: VariableTypeInput,
      value: string | number | boolean | null
    ): string | null => {
      if (variable.required && (value === null || value === undefined || value === '')) {
        return `${variable.name} is required`
      }

      if (variable.type === 'number' && value !== null && value !== undefined && value !== '') {
        const numValue = Number(value)
        const numberVar = variable as any

        if (isNaN(numValue)) {
          return 'Must be a valid number'
        }

        if (
          numberVar.minimum !== null &&
          numberVar.minimum !== undefined &&
          numValue < numberVar.minimum
        ) {
          return `Must be at least ${numberVar.minimum}`
        }

        if (
          numberVar.maximum !== null &&
          numberVar.maximum !== undefined &&
          numValue > numberVar.maximum
        ) {
          return `Must be at most ${numberVar.maximum}`
        }

        if (numberVar.whole_number && !Number.isInteger(numValue)) {
          return 'Must be a whole number'
        }
      }

      return null
    }

    const handleBlur = (boxId: string) => {
      const box = variableBoxes.find((b) => b.id === boxId)
      if (!box || !box.selectedVariableId) return

      const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
      if (!variable) return

      const error = validateValue(variable, box.value)
      if (error) {
        setErrors((prev) => ({ ...prev, [boxId]: error }))
      }
    }

    const renderValueInput = (box: VariableBox) => {
      if (!box.selectedVariableId) return null

      const variable = availableVariables.find((v) => v.id === box.selectedVariableId)
      if (!variable) return null

      const error = errors[box.id]
      const hasError = !!error

      switch (variable.type) {
        case 'boolean':
          return (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${box.id}-value`}
                checked={box.value === true}
                onCheckedChange={(checked) => updateVariableValue(box.id, checked)}
              />
              <Label htmlFor={`${box.id}-value`} className="text-sm">
                {box.value === true ? 'True' : 'False'}
              </Label>
            </div>
          )

        case 'number':
          const numberVar = variable as any
          return (
            <div className="space-y-1">
              <Input
                type="number"
                value={
                  typeof box.value === 'number'
                    ? box.value.toString()
                    : typeof box.value === 'boolean'
                      ? ''
                      : box.value || ''
                }
                onChange={(e) =>
                  updateVariableValue(box.id, e.target.value ? Number(e.target.value) : null)
                }
                onBlur={() => handleBlur(box.id)}
                min={numberVar.minimum || undefined}
                max={numberVar.maximum || undefined}
                step={numberVar.whole_number ? 1 : 'any'}
                className={hasError ? 'border-red-500' : ''}
                placeholder="Enter value"
              />
              {(numberVar.minimum !== null || numberVar.maximum !== null) && (
                <p className="text-xs text-gray-500">
                  {numberVar.minimum !== null && numberVar.maximum !== null
                    ? `Range: ${numberVar.minimum} - ${numberVar.maximum}`
                    : numberVar.minimum !== null
                      ? `Min: ${numberVar.minimum}`
                      : `Max: ${numberVar.maximum}`}
                  {numberVar.whole_number && ' (whole numbers)'}
                </p>
              )}
            </div>
          )

        case 'string':
          const stringVar = variable as any
          if (stringVar.options && stringVar.options.length > 0) {
            return (
              <Select
                value={String(box.value || '')}
                onValueChange={(newValue) => updateVariableValue(box.id, newValue)}
              >
                <SelectTrigger className={hasError ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select value" />
                </SelectTrigger>
                <SelectContent>
                  {stringVar.options.map((option: string) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
          return (
            <Input
              type="text"
              value={
                typeof box.value === 'string'
                  ? box.value
                  : typeof box.value === 'boolean'
                    ? ''
                    : String(box.value || '')
              }
              onChange={(e) => updateVariableValue(box.id, e.target.value)}
              onBlur={() => handleBlur(box.id)}
              className={hasError ? 'border-red-500' : ''}
              placeholder="Enter value"
            />
          )

        case 'binary':
          return (
            <Input
              type="text"
              value={
                typeof box.value === 'string'
                  ? box.value
                  : typeof box.value === 'boolean'
                    ? ''
                    : String(box.value || '')
              }
              onChange={(e) => updateVariableValue(box.id, e.target.value)}
              onBlur={() => handleBlur(box.id)}
              className={hasError ? 'border-red-500' : ''}
              placeholder="Enter binary value"
            />
          )

        default:
          return null
      }
    }

    const getUsedVariableIds = () => {
      return variableBoxes.map((box) => box.selectedVariableId).filter(Boolean) as string[]
    }

    const getAvailableVariablesForBox = (currentBoxId: string) => {
      const usedIds = variableBoxes
        .filter((box) => box.id !== currentBoxId)
        .map((box) => box.selectedVariableId)
        .filter(Boolean) as string[]

      return availableVariables.filter((variable) => !usedIds.includes(variable.id))
    }

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Variable Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {availableVariables.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No variables available</p>
          ) : (
            <>
              {variableBoxes.map((box) => {
                const selectedVariable = availableVariables.find(
                  (v) => v.id === box.selectedVariableId
                )
                const error = errors[box.id]
                const availableVarsForBox = getAvailableVariablesForBox(box.id)

                return (
                  <div key={box.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2">
                          <Select
                            value={box.selectedVariableId || ''}
                            onValueChange={(value) =>
                              updateVariableSelection(box.id, value || null)
                            }
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select a variable" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableVarsForBox.map((variable) => (
                                <SelectItem key={variable.id} value={variable.id}>
                                  <div className="flex items-center gap-2">
                                    <span>{variable.name}</span>
                                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                      {variable.type}
                                    </span>
                                    {variable.required && (
                                      <span className="text-red-500 text-xs">*</span>
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {selectedVariable && (
                          <>
                            {selectedVariable.description && (
                              <p className="text-xs text-gray-500">
                                {selectedVariable.description}
                              </p>
                            )}

                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium">Value:</Label>
                                {selectedVariable.required && (
                                  <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">
                                    Required
                                  </span>
                                )}
                              </div>
                              {renderValueInput(box)}
                            </div>
                          </>
                        )}

                        {error && (
                          <div className="flex items-center gap-1 text-red-500 text-xs">
                            <AlertCircle className="h-3 w-3" />
                            <span>{error}</span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVariableBox(box.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}

              <Button
                onClick={addVariableBox}
                variant="outline"
                className="w-full text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                disabled={getUsedVariableIds().length >= availableVariables.length}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Variable
              </Button>

              {getUsedVariableIds().length >= availableVariables.length && (
                <p className="text-xs text-gray-500 text-center">
                  All available variables have been added
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    )
  }
)

VariableSelector.displayName = 'VariableSelector'
